import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Layout } from './components/layout/Layout';
import { AdminLayout } from './components/layout/AdminLayout';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { useAuthStore } from './stores/authStore';
import { useSessionExpiry } from './hooks/useSessionExpiry';
import { SessionExpiryWarning } from './components/SessionExpiryWarning';
import { rememberReturnPath } from './lib/returnPath';

// Lazy-loaded pages for code splitting (Sprint 31 perf requirement: <250KB initial JS)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const OIDCCallback = lazy(() => import('./pages/OIDCCallback'));  // Sprint 31.9: OIDC callback
const SignupWizard = lazy(() => import('./pages/SignupWizard'));  // Sprint 31.9 Phase 2: Self-service signup
const SessionSelect = lazy(() => import('./pages/SessionSelect'));  // Sprint 31.9 Phase 11: Session selection
const TenantLogin = lazy(() => import('./pages/TenantLogin'));  // Sprint 34.10: Tenant-specific SSO login

// PKI Pages
const CertificatesBrowse = lazy(() => import('./pages/pki/CertificatesBrowse'));
const CertificateIssue = lazy(() => import('./pages/pki/CertificateIssue'));
const CertificateRevoke = lazy(() => import('./pages/pki/CertificateRevoke'));
const SshCertificates = lazy(() => import('./pages/pki/SshCertificates'));
const SshKeys = lazy(() => import('./pages/pki/SshKeys'));  // Sprint 26: SSH Keys page
const AcmeClients = lazy(() => import('./pages/pki/AcmeClients'));
const AcmeEabPreAuth = lazy(() => import('./pages/pki/AcmeEabPreAuth'));  // Sprint 11.2: EAB promoted to main nav

// Tools Pages (Sprint 34.9, Sprint 34.11)
const CertificateValidator = lazy(() => import('./pages/tools/CertificateValidator'));

// Admin Pages
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminGroups = lazy(() => import('./pages/admin/Groups'));
const AdminRoles = lazy(() => import('./pages/admin/Roles'));
const AdminPermissionSets = lazy(() => import('./pages/admin/PermissionSets'));
const AdminProfiles = lazy(() => import('./pages/admin/Profiles'));
const AdminTenants = lazy(() => import('./pages/admin/Tenants'));
const AdminDomains = lazy(() => import('./pages/admin/Domains'));
const AdminTokens = lazy(() => import('./pages/admin/Tokens'));
const AdminIntegrations = lazy(() => import('./pages/admin/Integrations'));
const AdminBackups = lazy(() => import('./pages/admin/Backups'));
const AdminRotations = lazy(() => import('./pages/admin/Rotations'));
const AdminQueue = lazy(() => import('./pages/admin/Queue'));
const AdminHealth = lazy(() => import('./pages/admin/Health'));
const AdminSshPolicies = lazy(() => import('./pages/admin/SshPolicies'));
const SshHosts = lazy(() => import('./pages/pki/SshHosts'));  // Sprint 28: SSH Host Inventory moved to PKI nav
const AdminActivity = lazy(() => import('./pages/admin/Activity'));
const AdminAudit = lazy(() => import('./pages/admin/Audit'));
const AdminHistory = lazy(() => import('./pages/admin/History'));
const AdminAuditExport = lazy(() => import('./pages/admin/AuditExport'));
const AdminOverview = lazy(() => import('./pages/admin/Overview'));
const AdminPermissions = lazy(() => import('./pages/admin/Permissions'));
const AdminSecurity = lazy(() => import('./pages/admin/Security'));
const AdminEnvironment = lazy(() => import('./pages/admin/Environment'));
const AdminRateLimits = lazy(() => import('./pages/admin/RateLimits'));
const AdminEmail = lazy(() => import('./pages/admin/Email'));
// AdminPreAuth and AdminEabCredentials removed — Sprint 11.2: EAB promoted to /pki/eab (AcmeEabPreAuth)
const AdminPlatformMetrics = lazy(() => import('./pages/admin/PlatformMetrics'));  // Sprint 31.11
const AdminOidcProviders = lazy(() => import('./pages/admin/OidcProviders'));  // Sprint 31.11 Phase 3
const AdminKeyManagement = lazy(() => import('./pages/admin/KeyManagement'));  // Sprint 31.15b
const AdminBilling = lazy(() => import('./pages/admin/Billing'));  // Sprint 32: Billing administration
const AdminTenantBilling = lazy(() => import('./pages/admin/TenantBilling'));  // Sprint 32: Tenant billing & add-ons
const AdminCompliance = lazy(() => import('./pages/admin/Compliance'));  // Sprint 9.2: Rewritten without MUI
// Sprint 34.15: IdentityProviders page removed - consolidated into Integrations wizard
const AdminOrganizations = lazy(() => import('./pages/admin/Organizations'));  // Sprint 34.14: VortexCA Organizations
const AdminIdentityCenter = lazy(() => import('./pages/admin/IdentityCenter'));  // Sprint 34.14: Identity Center IC groups
const AdminPermissionGrid = lazy(() => import('./pages/admin/PermissionGrid'));  // Sprint 34.14: Permission assignment grid
const AdminConsolidatedBilling = lazy(() => import('./pages/admin/ConsolidatedBilling'));  // Sprint 34.14: Org consolidated billing

// User Profile (Sprint 11.4)
const UserProfile = lazy(() => import('./pages/UserProfile'));

// Bootstrap Wizard (Sprint 31.2)
const BootstrapWizard = lazy(() => import('./pages/BootstrapWizard'));

// MFA Pages (Sprint 41 Phase 3.1.7)
const MfaChallenge = lazy(() => import('./pages/MfaChallenge'));
const MfaEnroll = lazy(() => import('./pages/MfaEnroll'));
const AdminMfaSettings = lazy(() => import('./pages/admin/MfaSettings'));
const AdminAuthentication = lazy(() => import('./pages/admin/Authentication'));
const AdminPkiSettings = lazy(() => import('./pages/admin/PkiSettings'));  // Sprint 43: Tenant PKI Settings
const AdminValidityCeilings = lazy(() => import('./pages/admin/ValidityCeilings'));  // Sprint 11.1: SC-081v3 Validity Ceilings

// Bootstrap detection guard - redirects to wizard if PKI not bootstrapped
function BootstrapGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [bootstrapChecked, setBootstrapChecked] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(true); // Assume true to avoid flash

  useEffect(() => {
    // Skip check if already on bootstrap page
    if (location.pathname === '/bootstrap') {
      setBootstrapChecked(true);
      return;
    }

    const checkBootstrap = async () => {
      try {
        const response = await fetch('/api/v1/admin/pki/bootstrap/state');
        if (response.ok) {
          const data = await response.json();
          // States that require bootstrap wizard:
          // - "unconfigured": Fresh install, no config chosen yet
          // - "waiting_root": Waiting for root CA upload
          // - "waiting_intermediate": Waiting for intermediate CA/CSR
          // "ready" means bootstrap IS complete (activated)
          const needsBootstrap = ['unconfigured', 'waiting_root', 'waiting_intermediate'].includes(data.state);
          if (needsBootstrap) {
            setIsBootstrapped(false);
            navigate('/bootstrap', { replace: true });
          }
        }
      } catch {
        // If bootstrap check fails, continue to app (might be network issue)
        console.warn('Bootstrap state check failed');
      } finally {
        setBootstrapChecked(true);
      }
    };

    checkBootstrap();
  }, [location.pathname, navigate]);

  if (!bootstrapChecked) {
    return <PageLoader />;
  }

  // If not bootstrapped and not on bootstrap page, the useEffect will redirect
  if (!isBootstrapped && location.pathname !== '/bootstrap') {
    return <PageLoader />;
  }

  return <>{children}</>;
}

// Protected route wrapper - handles auth state, continuous expiry, and 401 events
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const logout = useAuthStore((state) => state.logout);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const navigate = useNavigate();

  // Refresh effective permissions on session restore (page reload with existing token)
  // This ensures sidebar shows correct menu items based on server-side permissions
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const refreshPermissions = async () => {
      try {
        const response = await fetch('/api/v1/me/effective-permissions', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json() as {
            permissions: Array<{ name: string; source: string }>;
          };
          const permissions = data.permissions.map(p => p.name);
          setPermissions(permissions);
        } else if (response.status === 401) {
          // Token is invalid/expired, logout -- same stash as the event path above.
          rememberReturnPath(window.location.pathname + window.location.search);
          logout();
          sessionStorage.removeItem('access_token');
          navigate('/login', { replace: true });
        }
      } catch {
        // Network error - permissions will remain as-is (could be stale)
        console.warn('Failed to refresh permissions');
      }
    };

    refreshPermissions();
  }, [isAuthenticated, token, setPermissions, logout, navigate]);

  // Listen for 401 unauthorized events from API calls and redirect to login
  // This handles cases where the JWT expires mid-session.
  // Note: 401 = authentication failure (token issues) -> redirect to login
  //       403 = authorization failure (no permission) -> show error, don't logout
  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      // Log the auth failure reason for debugging (if provided)
      const detail = (event as CustomEvent).detail;
      if (detail) {
        console.debug('Auth failure:', detail);
      }
      // S49.4: stash the route here too. ⚠️ Verified on staging that this path did NOT do
      // so while the timer path did, so an expiry detected by a 401 dropped the user on the
      // dashboard while an expiry detected by the timer returned them to their page. Two
      // routes out of the same state behaving differently is worse than neither doing it,
      // because it looks like the feature is broken at random.
      rememberReturnPath(window.location.pathname + window.location.search);
      // Clear auth state which will trigger redirect via isAuthenticated check
      logout();
      // Also clear sessionStorage token to ensure clean state
      sessionStorage.removeItem('access_token');
      // Navigate to login page
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [logout, navigate]);

  // S49.2: continuous expiry detection. Before this, the ONLY reliable detector in the product
  // was the mount effect above -- which is why a page refresh redirected correctly and sitting
  // on a page did not. See hooks/useSessionExpiry.ts for the three causes.
  const { isWarning, secondsRemaining, dismissWarning, endSession, extendSession } =
    useSessionExpiry();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return (
    <>
      <SessionExpiryWarning
        open={isWarning}
        secondsRemaining={secondsRemaining}
        onDismiss={dismissWarning}
        onSignInNow={endSession}
        onExtend={extendSession}
      />
      {children}
    </>
  );
}

// Loading fallback for lazy-loaded components
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <BootstrapGuard>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/login/t/:tenantSlug" element={<TenantLogin />} />  {/* Sprint 34.10: Tenant SSO login */}
          <Route path="/auth/callback" element={<OIDCCallback />} />  {/* Sprint 31.9: OIDC callback from API */}
          <Route path="/auth/callback/:provider" element={<OIDCCallback />} />  {/* Sprint 31.9: OIDC callback (legacy) */}
          <Route path="/signup/new-tenant" element={<SignupWizard />} />  {/* Sprint 31.9 Phase 2: Self-service signup */}
          <Route path="/bootstrap" element={<BootstrapWizard />} />
          <Route path="/select-session" element={<SessionSelect />} />  {/* Sprint 31.9 Phase 11: Session selection */}
          <Route path="/mfa-challenge" element={<MfaChallenge />} />  {/* Sprint 41 Phase 3.1.7: MFA TOTP challenge */}
          
          {/* Protected routes with layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<Dashboard />} />
            
            {/* PKI Section */}
            <Route path="pki">
              <Route path="certificates" element={<CertificatesBrowse />} />
              <Route path="certificates/issue" element={<CertificateIssue />} />
              <Route path="certificates/:serial" element={<CertificatesBrowse />} />
              <Route path="certificates/:serial/revoke" element={<CertificateRevoke />} />
              <Route path="ssh" element={<SshCertificates />} />
              <Route path="ssh-keys" element={<SshKeys />} />
              <Route path="ssh-hosts" element={<SshHosts />} />  {/* Sprint 28: SSH Host Inventory promoted to PKI */}
              <Route path="acme" element={<AcmeClients />} />
              <Route path="eab" element={<AcmeEabPreAuth />} />  {/* Sprint 11.2: EAB promoted to main nav */}
            </Route>
            
            {/* Tools Section (Sprint 34.9, Sprint 34.11) */}
            <Route path="tools">
              <Route path="validator" element={<CertificateValidator />} />
            </Route>
            
            {/* User Profile (Sprint 11.4) */}
            <Route path="profile" element={<UserProfile />} />
            
            {/* Management Section - uses /manage/* to avoid conflict with API /admin/* routes */}
            {/* Sprint 31.7: Wrapped with AdminLayout for mockup-1 parity (sub-sidebar nav) */}
            {/* Sprint 31.11 Phase 4: Platform routes under /manage/platform/*, tenant routes at /manage/* */}
            <Route path="manage" element={<AdminLayout />}>
              {/* Shared route - both platform and tenant admins */}
              <Route path="overview" element={<AdminOverview />} />
              
              {/* Platform Admin Routes - /manage/platform/* */}
              <Route path="platform/tenants" element={<AdminTenants />} />
              <Route path="platform/metrics" element={<AdminPlatformMetrics />} />
              <Route path="platform/oidc-providers" element={<AdminOidcProviders />} />
              <Route path="platform/backups" element={<AdminBackups />} />
              <Route path="platform/queue" element={<AdminQueue />} />
              <Route path="platform/health" element={<AdminHealth />} />
              <Route path="platform/environment" element={<AdminEnvironment />} />
              <Route path="platform/security" element={<AdminSecurity />} />
              <Route path="platform/rate-limits" element={<AdminRateLimits />} />
              <Route path="platform/email" element={<AdminEmail />} />
              <Route path="platform/billing" element={<AdminBilling />} />
              <Route path="platform/validity-ceilings" element={<AdminValidityCeilings />} />  {/* Sprint 11.1: SC-081v3 */}
              <Route path="platform/organizations" element={<AdminOrganizations />} />  {/* Sprint 34.14: Organizations */}
              <Route path="platform/organizations/:orgId/identity-center" element={<AdminIdentityCenter />} />  {/* Sprint 34.14: IC groups */}
              <Route path="platform/organizations/:orgId/permission-grid" element={<AdminPermissionGrid />} />  {/* Sprint 34.14: Permission grid */}
              <Route path="platform/organizations/:orgId/billing" element={<AdminConsolidatedBilling />} />  {/* Sprint 34.14: Consolidated billing */}
              
              {/* Tenant Admin Routes - /manage/* */}
              <Route path="users" element={<AdminUsers />} />
              <Route path="groups" element={<AdminGroups />} />
              <Route path="roles" element={<AdminRoles />} />
              <Route path="permission-sets" element={<AdminPermissionSets />} />
              <Route path="organizations" element={<AdminOrganizations />} />  {/* Sprint 34.15: Org-type tenant access */}
              <Route path="organizations/:orgId/identity-center" element={<AdminIdentityCenter />} />  {/* Sprint 34.15: IC groups for tenant admins */}
              <Route path="organizations/:orgId/permission-grid" element={<AdminPermissionGrid />} />  {/* Sprint 34.15: Permission grid for tenant admins */}
              <Route path="organizations/:orgId/billing" element={<AdminConsolidatedBilling />} />  {/* Sprint 34.15: Consolidated billing for tenant admins */}
              <Route path="profiles" element={<AdminProfiles />} />
              <Route path="domains" element={<AdminDomains />} />
              <Route path="pki-settings" element={<AdminPkiSettings />} />  {/* Sprint 43: Tenant PKI Settings */}
              <Route path="tokens" element={<AdminTokens />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              {/* Sprint 34.15: identity-providers route removed - consolidated into Integrations wizard */}
              <Route path="key-management" element={<AdminKeyManagement />} />
              <Route path="billing" element={<AdminTenantBilling />} />
              <Route path="rotations" element={<AdminRotations />} />
              <Route path="ssh-policies" element={<AdminSshPolicies />} />
              <Route path="ssh-hosts" element={<Navigate to="/pki/ssh-hosts" replace />} />  {/* Sprint 28: Redirect to PKI */}
              {/* Sprint 34.18: SSH CA Settings removed - redundant with Browse SSH Certs page */}
              <Route path="preauth" element={<Navigate to="/pki/eab" replace />} />  {/* Sprint 11.2 (S11.2.11): PreAuth deprecated, redirect to promoted EAB page */}
              <Route path="eab" element={<Navigate to="/pki/eab" replace />} />  {/* Sprint 11.2: Redirects to promoted location */}
              <Route path="activity" element={<AdminActivity />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="history" element={<AdminHistory />} />
              <Route path="export-audit" element={<AdminAuditExport />} />
              <Route path="compliance" element={<AdminCompliance />} />  {/* Sprint 9.2: Compliance dashboard */}
              <Route path="permissions" element={<AdminPermissions />} />
              
              {/* Legacy routes for backward compatibility - redirect to new paths */}
              <Route path="tenants" element={<Navigate to="/manage/platform/tenants" replace />} />
              <Route path="backups" element={<Navigate to="/manage/platform/backups" replace />} />
              <Route path="queue" element={<Navigate to="/manage/platform/queue" replace />} />
              <Route path="health" element={<Navigate to="/manage/platform/health" replace />} />
              <Route path="environment" element={<Navigate to="/manage/platform/environment" replace />} />
              <Route path="security" element={<Navigate to="/manage/platform/security" replace />} />
              <Route path="rate-limits" element={<Navigate to="/manage/platform/rate-limits" replace />} />
              <Route path="email" element={<Navigate to="/manage/platform/email" replace />} />              
              {/* Sprint 41 Phase 3.1.7: MFA management pages */}
              {/* S48d.4: kept, NOT deleted -- bookmarks and muscle memory exist, and a 404
                  on a page someone used yesterday is a worse outcome than a duplicate route.
                  The Profile page is now the documented home; this stays reachable. */}
              <Route path="mfa-settings" element={<AdminMfaSettings />} />
              <Route path="authentication" element={<AdminAuthentication />} />
              <Route path="mfa-enroll" element={<MfaEnroll />} />            </Route>
          </Route>
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BootstrapGuard>
  );
}
